import { millis } from '../../shared/time-utils'
import i18n from '../i18n/i18n'

var external_scripts = {}

const flushExternalScriptCallbacks = function(url) {
  return function() {
    var callbacks = external_scripts[url]
    if (callbacks && callbacks.length) {
      var cb
      while ((cb = callbacks.pop())) {
        if (typeof cb === 'function') {
          cb()
        }
      }
      external_scripts[url] = 1 // mark as 'already loaded'
    }
  }
}

const IS_MAC = navigator.platform.toUpperCase().indexOf('MAC') >= 0

const HTML_ENTITIES = {
  '&': '&amp;',
  "'": '&#39;',
  '"': '&quot;',
  '<': '&lt;',
  '>': '&gt;'
}

const MMM_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MM_MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/

 
const HISTOGRAM_GRANULARITY = [1, 2, 4] // granularity in hours
const RETENTION_DAYS = {
  MAPLE: 14,
  OAK: 30
}
 

export default {
  getQueryString(field, url) {
    var string = new RegExp('[?&]' + field + '=([^&#]*)', 'i').exec(url || window.location.href)
    return string ? string[1] : null
  },
  escapeRegExp(text) {
    return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
  },
  encodeHTML(text) {
    if (!text) {
      return text
    }

    const re = new RegExp(Object.keys(HTML_ENTITIES).join('|'), 'g')
    return text.replace(re, match => HTML_ENTITIES[match])
  },
  decodeHTML(text) {
    if (!text) {
      return text
    }

    Object.keys(HTML_ENTITIES).forEach((k) => {
      const re = new RegExp(HTML_ENTITIES[k], 'gi')
      text = text.replace(re, () => k)
    })
    return text
  },
  JSONParse(json) {
    let parsedValue = {}

    try {
      parsedValue = JSON.parse(json)
    } catch (e) {
      return null
    }

    return parsedValue
  },
  queryStringify(obj) {
    if (!obj) {
      return ''
    }
    if (typeof obj !== 'object') {
      return obj.toString()
    }
    var qs = []
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (obj[key] instanceof Array) {
          if (obj[key].length) {
            qs.push(key + '=' + obj[key].map(encodeURIComponent).join(','))
          }
        } else if (obj[key]) {
          qs.push(key + '=' + encodeURIComponent(obj[key]))
        }
      }
    }
    return qs.length > 0 ? qs.join('&') : ''
  },
  capitalize(str) {
    if (typeof str !== 'string') {
      return str.toString()
    }
    return str.charAt(0).toUpperCase() + str.slice(1)
  },
  pluralize(num, zero, one, many) {
    if (num === 0) {
      return zero || num // fallback to just showing the number
    }
    if (num === 1) {
      return one || zero // fallback to zero if no 'one' form is given
    }
    if (num > 1) {
      return many || zero // fallback to zero form is no 'many' form is given
    }
    return num // default to return just the number
  },
  truncate(str, maxlen, end, symbol) {
    if (typeof end === 'string') {
      // assume the form of (str, maxlen, symbol); -- left handed truncate
      symbol = end
      end = 0
    }
    end = Math.min(maxlen, end || 0)
    symbol = symbol || '…'
    var start = end > 0 ? maxlen - end : maxlen
    if (str && str.length > maxlen + 1) {
      // extra character for the symbol
      return str.substring(0, start) + symbol + str.substring(str.length - end - 1)
    }
    return str
  },
  stringSplice(str, start, end, replace) {
    var first = str.slice(0, start)
    var second = str.slice(end)
    return first + replace + second
  },
  selectByValue(selectEl, value) {
    if (selectEl && selectEl.options) {
      for (var i = selectEl.options.length - 1; i >= 0; i--) {
        if (selectEl.options[i].value === value) {
          selectEl.selectedIndex = i
          break
        }
      }
    }
  },
  loadScripts(urls, done) {
    if (typeof urls === 'string') {
      urls = [urls]
    }
    var pending = urls.length
    if (!pending) {
      done()
    }
    var callback = function() {
      pending -= 1
      if (!pending) {
        done()
      }
    }
    for (var i = 0, len = urls.length; i < len; i++) {
      if (external_scripts[urls[i]]) {
        if (external_scripts[urls[i]] === 1) {
          // already loaded
          callback()
        } else {
          external_scripts[urls[i]].unshift(callback)
        }
        continue
      }
      external_scripts[urls[i]] = [callback]
      var script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = urls[i]
      script.onload = flushExternalScriptCallbacks(urls[i])
      document.body.appendChild(script)
    }
  },
  getModifierKey() {
    if (IS_MAC) {
      return {
        prop: 'metaKey',
        symbol: 'cmd'
      }
    } else {
      return {
        prop: 'ctrlKey',
        symbol: 'Ctrl'
      }
    }
  },
  // replace string value in array of strings
  replaceInArray(arr, value, newValue) {
    if (typeof value !== 'string' || typeof newValue !== 'string' || !Array.isArray(arr)) {
      return arr
    }

    return arr.map((item) => {
      if (typeof item === 'string' && item.toLowerCase() === value.toLowerCase()) {
        return newValue
      }
      return item
    })
  },

  /**
   * More performant native date formatting for Log lines.
   * Matches moment.js output in 100% of cases while performing 4-4.5 times faster.
   *
   * @param ts Timestamp
   * @param format Date format (default, python, ncsa, rfc3339)
   * @param useUTC Boolean
   */
  logTimeFormat(ts, format, useUTC) {
    const FORMAT_DEFAULT = 'default'
    const FORMAT_PYTHON = 'python'
    const FORMAT_NCSA = 'ncsa'
    const FORMAT_RFC3339 = 'rfc3339'

    const date = new Date(ts)

    // Common tokens
    const HH = (useUTC ? date.getUTCHours() : date.getHours()).toString().padStart(2, '0')
    const mm = (useUTC ? date.getUTCMinutes() : date.getMinutes()).toString().padStart(2, '0')
    const ss = (useUTC ? date.getUTCSeconds() : date.getSeconds()).toString().padStart(2, '0')
    const D = useUTC ? date.getUTCDate() : date.getDate()

    if (format === FORMAT_DEFAULT) {
      const MMM = MMM_MONTHS[useUTC ? date.getUTCMonth() : date.getMonth()]
      return `${MMM} ${D} ${HH}:${mm}:${ss}`
    } else if (format === FORMAT_PYTHON) {
      const YYYY = useUTC ? date.getUTCFullYear() : date.getFullYear()
      const MM = MM_MONTHS[useUTC ? date.getUTCMonth() : date.getMonth()]
      const DD = D.toString().padStart(2, '0')
      return `${YYYY}-${MM}-${DD} ${HH}:${mm}:${ss}`
    } else if (format === FORMAT_NCSA) {
      const MMM = MMM_MONTHS[useUTC ? date.getUTCMonth() : date.getMonth()]
      const YYYY = useUTC ? date.getUTCFullYear() : date.getFullYear()
      return `${D}/${MMM}/${YYYY}:${HH}:${mm}:${ss}`
    } else if (format === FORMAT_RFC3339) {
      const YYYY = useUTC ? date.getUTCFullYear() : date.getFullYear()
      const MM = MM_MONTHS[useUTC ? date.getUTCMonth() : date.getMonth()]
      const DD = D.toString().padStart(2, '0')

      const MILLISECONDS_LENGTH = 3
      const SSS = (useUTC ? date.getUTCMilliseconds() : date.getMilliseconds()).toString().padStart(MILLISECONDS_LENGTH, '0')
      return `${YYYY}-${MM}-${DD} ${HH}:${mm}:${ss}.${SSS}`
    } else {
      return 'logTimeFormat(): Invalid parameter format.'
    }
  },
  getRoundedNow() {
    const now = new Date()

    now.setHours(now.getHours(), now.getMinutes(), 0, 0) // zero out seconds/milliseconds
    return now.getTime()
  },
  naturalSortBy(property = 'name') {
    // for current use cases, property is either 'name' or 'title'
    return (a, b) => {
      if (typeof a === 'undefined' || typeof a[property] === 'undefined') {
        return 1
      }
      if (typeof b === 'undefined' || typeof b[property] === 'undefined') {
        return -1
      }

      const _A = a[property].toUpperCase()
      const _B = b[property].toUpperCase()

      // sort so that 'New View 10' comes after 'New View 9' (instead of 'New View 1')
      return _A.localeCompare(_B, undefined, { numeric: true, sensitivity: 'base' })
    }
  },
  formatCurrency(value) {
    if (!value) {
      return
    }

    if (typeof value === 'number') {
      value = value.toString()
    }

    let newValue

    if (!value.includes('.')) {
      newValue = `${value}.00`
    } else {
      let integer = value.split('.')[0]
      let decimal = value.split('.')[1]

      // make sure integer shows at least 1 digit
      if (integer.length === 0) {
        integer = 0
      }

      // make sure decimal shows 2 digits
      if (decimal.length === 1) {
        decimal = `${decimal}0`
      } else if (decimal.length > 2) {
        decimal = decimal.slice(0, 2)
      }

      newValue = `${integer}.${decimal}`
    }

    return newValue
  },
  getType(value) {
    const INVALID = 'invalid'

    if (!value) {
      return INVALID
    } else if (Array.isArray(value)) {
      return 'arr'
    } else if (value && value.constructor.name === 'Object') {
      return 'obj'
    } else if (typeof value === 'number') {
      return 'number'
    } else if (typeof value === 'string') {
      return 'string'
    } else {
      return INVALID
    }
  },
  hasLocalStorage() {
    return window.localStorage && typeof localStorage.setItem === 'function'
  },
  chronoSortFn(a, b) {
    if (!a._ts && !a._id) { return 1 }
    if (!b._ts && !b._id) { return -1 }
    if (a._ts < b._ts) { return -1 }
    if (a._ts > b._ts) { return 1 }
    if (a._ts === b._ts) {
      if (a._id < b._id) { return -1 }
      if (a._id > b._id) { return 1 }
    }
    return 0
  },
  splitQueryParam(param) {
    return typeof param === 'string' ? param.split(',').map(decodeURIComponent) : param || []
  },
  calculateRetentionDays(retentionLimit) {
    return (new Date().getTime() - retentionLimit) / millis('1d')
  },
  // this matches how granularity is calculated in logdna-api lib/metrics.js
  calculateGranularity(retentionLimit) {
    const retentionDays = this.calculateRetentionDays(retentionLimit)
    let granularity = HISTOGRAM_GRANULARITY[2] // 4h interval

    if (retentionDays < RETENTION_DAYS.MAPLE) {
      granularity = HISTOGRAM_GRANULARITY[0] // 1h interval

    } else if (retentionDays < RETENTION_DAYS.OAK) {
      granularity = HISTOGRAM_GRANULARITY[1] // 2h interval
    }

    return granularity * millis('1h')
  },
  validateEmail(value) {
    return EMAIL_REGEX.test(value)
  },
  intlListFormat(array, type = 'conjunction') {
    const TYPES = {
      conjunction: 'and',
      disjunction: 'or',
      unit: ' '
    }

    if (!TYPES[type]) {
      throw new Error('Invalid Intl.ListFormat type')
    }

    if (Intl && Intl.ListFormat) {
      const formatter = new Intl.ListFormat(i18n.language, { style: 'long', type })
      return formatter.format(array)
    }
    if (array.length <= 2) {
      return array.join(` ${TYPES[type]} `)
    }

    const last = array.pop()
    if (type === 'unit') {
      return `${array.join(', ')}, ${last}`
    }
    return `${array.join(', ')}, ${TYPES[type]} ${last}`
  }
}
